import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import SetOrderDialog from './components/SetOrderDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import {
	DialogClickToType,
	DialogContentType,
	OnoffType,
	ShowTimeType,
} from '@/api/enum/common.enum';
import InformationListSummaryFilterSection from './components/DialogListSummaryFilterSection';
import { showLabel } from '@/utils/helper';
import { useGetDialogListQuery } from '@/api/app-setting/dialogApiSlice';
import {
	DialogPageFilter,
	PopupMessageDTO,
} from '@/api/DTO/app-setting/dialog.interface';
import PublishedChip from '@/common/components/PublishedChip';
import { PUBLISHED_LISTB } from '@/common/constants/common';

export interface DataGrid {
	updatedAt: string;
	publishedEndAt: string;
	publishedStartAt: string;
}

export default function InformationListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const showTimeTypeList = [
		{ id: ShowTimeType.Every, name: t('dialogList.every') },
		{ id: ShowTimeType.Once, name: t('dialogList.once') },
		{ id: ShowTimeType.One, name: t('dialogList.one') },
	];
	const dialogContentTypeList = [
		{ id: DialogContentType.Image, name: t('dialogList.image') },
		{ id: DialogContentType.ImageText, name: t('dialogList.imageText') },
		{ id: DialogContentType.Video, name: t('dialogList.video') },
	];
	const dialogClickToTypeList = [
		{ id: DialogClickToType.Null, name: t('dialogList.null') },
		{ id: DialogClickToType.OutSide, name: t('dialogList.outSide') },
		{ id: DialogClickToType.InSide, name: t('dialogList.inSide') },
	];

	const defaultPropertyFilter: DialogPageFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
		publishedList: [],
	};

	const defaultValues: DefaultValues<DialogPageFilter> = defaultPropertyFilter;
	const methods = useForm<DialogPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: DialogPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
			'publishedList',
		]),
	};
	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetDialogListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: PopupMessageDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				publishedStartAt: !item.publishedStartAt
					? '-'
					: dayjs(item.publishedStartAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				publishedEndAt: !item.publishedEndAt
					? '-'
					: dayjs(item.publishedEndAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as DialogPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('dialogList.dialogTitle'),
			flex: 10,
		},
		{
			field: 'showPageNames',
			headerName: t('dialogList.showPage'),
			flex: 10,
		},
		{
			field: 'type',
			headerName: t('dialogList.showTime'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.type, showTimeTypeList),
		},
		{
			field: 'publishedAndroid',
			headerName: t('dialogList.platform'),
			flex: 10,
			renderCell: (params: any) => {
				if (params.row.publishedAndroid && params.row.publishedIos) {
					return 'iOS, Android';
				} else if (params.row.publishedAndroid) {
					return 'Android';
				} else if (params.row.publishedIos) {
					return 'iOS';
				} else {
					return '-';
				}
			},
		},
		{
			field: 'mediaType',
			headerName: t('dialogList.contentType'),
			flex: 10,
			renderCell: (params: any) =>
				showLabel(params.row.mediaType, dialogContentTypeList),
		},
		{
			field: 'linkType',
			headerName: t('dialogList.clickTo'),
			flex: 10,
			renderCell: (params: any) =>
				showLabel(params.row.linkType, dialogClickToTypeList),
		},
		{
			field: 'published',
			headerName: t('dialogList.dialogStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('dialogList.pushedTime'),
			flex: 10,
		},
		{
			field: 'publishedEndAt',
			headerName: t('dialogList.pulledTime'),
			flex: 10,
		},
		{
			field: 'updatedAt',
			headerName: t('dialogList.updateTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/app-setting/dialog-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.dialogList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<SetOrderDialog />
					<Button
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<NoteAltOutlinedIcon />}
						onClick={() => navigate(`/app-setting/dialog-list/add`)}
					>
						{t('button.add')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<InformationListSummaryFilterSection></InformationListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
